import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Flower, Sun, Drop } from "@phosphor-icons/react";
import Reveal from "../components/Reveal";
import ExploreInDepth from "../components/ExploreInDepth";
import { specialities, pillars } from "../data/site";
import { useMedia } from "../lib/media";
import { useTreatments } from "../lib/treatments";
import useSeo from "../lib/useSeo";
import { useT, usePick } from "../lib/i18n";

const pillarIcons = { "Herbal Rasayanas": Leaf, "Vajikarana": Flower, "Diet (Ahara)": Drop, "Lifestyle (Vihara)": Sun };

export default function Treatments() {
  useSeo({
    title: "Men's Ayurvedic Treatments | Yuva Ayurvedic Clinic Prayagraj",
    absoluteTitle: true,
    description: "Classical Ayurvedic treatments for men's sexual health in Prayagraj — premature ejaculation (shighrapatan), erectile dysfunction (napunsakta), night-fall, mardana kamzori, sperm count and more. 100% confidential, no side effects.",
    keywords: "Ayurvedic treatment for erectile dysfunction India, Shighrapatan ka ilaaj Prayagraj, Napunsakta ka Ayurvedic ilaaj, Sexual weakness ka Ayurvedic upay Prayagraj, Natural cure for sexual weakness India, Male sexual wellness Ayurveda India",
  });
  const t = useT();
  const pick = usePick();
  const { map: mediaMap } = useMedia();
  const { items: dynamicTreatments } = useTreatments();
  const list = dynamicTreatments.length > 0 ? dynamicTreatments : specialities;
  return (
    <div className="pb-24" data-testid="treatments-page">
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overline">{t("tx.overline")}</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-4xl leading-[0.95]">
              {t("tx.title.l1")}<br/>{t("tx.title.l2")} <span className="italic text-terracotta">{t("tx.title.l3")}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              {t("tx.subtitle")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-2 gap-10">
          {list.map((s, i) => {
            const img = mediaMap[`spec_${s.slug}`] || s.image;
            return (
            <Reveal key={s.slug} delay={i * 0.06}>
              <article className="group bg-ivory border border-border/50 rounded-2xl overflow-hidden" data-testid={`treatment-${s.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={img} alt={`${pick(s, "title")} — confidential classical Ayurvedic treatment at Yuva Ayurvedic Clinic Prayagraj India`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="p-8">
                  <div className="overline">{t("tx.therapyLabel")}</div>
                  <h2 className="font-serif-display text-3xl mt-3 leading-tight">{pick(s, "title")}</h2>
                  <p className="mt-3 text-muted-foreground">{pick(s, "short")}</p>
                  <Link to="/book-appointment" className="mt-6 inline-flex items-center gap-2 text-terracotta text-sm uppercase tracking-[0.22em] hover:gap-3 transition-all">
                    {t("cta.consult")} <ArrowRight size={14}/>
                  </Link>
                </div>
              </article>
            </Reveal>
          );})}
        </div>
      </section>

      <section className="py-24 bg-ivory/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overline">{t("tx.pillars.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 leading-[1]">{t("tx.pillars.title.l1")} <span className="italic text-terracotta">{t("tx.pillars.title.l2")}</span></h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-4 gap-6">
            {pillars.map((p, i) => {
              const Icon = pillarIcons[p.title] || Leaf;
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div className="bg-cream border border-border/60 rounded-2xl p-8 h-full">
                    <Icon size={28} weight="duotone" className="text-sage"/>
                    <h3 className="font-serif-display text-2xl mt-4">{p.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <ExploreInDepth
        heading="Explore condition-specific Ayurvedic treatment pages"
        subheading="Each dedicated page below covers one specific concern in depth — symptoms, causes, our classical Ayurvedic protocol and answers to the most common patient questions."
      />
    </div>
  );
}
